var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useQuery, } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
/**
 * Call the dataProvider.getLocks() method and return the resolved locks array,
 * as well as the loading state.
 *
 * @param {string} resource
 * @param {GetLocksParams} params The getLocks parameters { meta }
 * @param {Object} options React Query Options object
 *
 * @returns The locks array for the given resource. Destructure as { data, error, isPending, isFetching }.
 *
 * @example Simple Usage
 *
 * ```tsx
 * const { data } = useGetLocks('posts');
 * ```
 *
 * @example Getting resource from context
 *
 * ```tsx
 * const resource = useResourceContext();
 * const { data: locks } = useGetLocks(resource);
 * ```
 *
 * @example Custom Datagrid
 *
 * ```tsx
 * const LockableEditButton = ({ locks }: { locks: Lock[] }) => {
 *     const id = useGetRecordId();
 *     const lock = locks.find(l => l.recordId === id);
 *
 *     if (!!lock) {
 *         const title = `This Post is locked by ${lock.identity}`;
 *
 *         return (
 *             <Tooltip title={title} aria-label={title} arrow>
 *                 <span>
 *                     <EditButton />
 *                 </span>
 *             </Tooltip>
 *         );
 *     }
 *
 *     return <EditButton />;
 * };
 *
 * const MyPostTitle = ({ label, locks }: { label: string; locks: Lock[] }) => {
 *     const id = useGetRecordId();
 *     const lock = locks.find(l => l.recordId === id);
 *
 *     return (
 *         <WrapperField label={label}>
 *             <TextField source="title" />
 *             {lock && (
 *                 <span style={{ color: 'red' }}>
 *                     {` (Locked by ${lock.identity})`}
 *                 </span>
 *             )}
 *         </WrapperField>
 *     );
 * };
 *
 * const MyPostActions = ({ label, locks }: { label: string; locks: Lock[] }) => {
 *     const id = useGetRecordId();
 *     const lock = locks.find(l => l.recordId === id);
 *
 *     return (
 *         <WrapperField label={label}>
 *             <DeleteButton disabled={!!lock} />
 *             <LockableEditButton locks={locks} />
 *         </WrapperField>
 *     );
 * };
 *
 * const MyPostGrid = () => {
 *     const resource = useResourceContext();
 *     const { data: locks } = useGetLocks(resource);
 *     return (
 *         <Datagrid
 *             bulkActionButtons={false}
 *             sx={{
 *                 '& .MuiTableCell-root:last-child': {
 *                     textAlign: 'right',
 *                 },
 *             }}
 *         >
 *             <MyPostTitle label="Title" locks={locks} />
 *             <MyPostActions label="Actions" locks={locks} />
 *         </Datagrid>
 *     );
 * };
 * ```
 *
 */
export function useGetLocks(resource, params, options) {
    if (params === void 0) { params = {}; }
    if (options === void 0) { options = {}; }
    var dataProvider = useDataProvider();
    return useQuery(__assign({ queryKey: [resource, 'getLocks', params], queryFn: function () {
            return dataProvider.getLocks(resource, params).then(function (_a) {
                var data = _a.data;
                return data;
            });
        } }, options));
}
